<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <TransitionRoot as="template" :show="Boolean(cartStore.error)">
    <Dialog as="div" class="relative z-50" @close="open = false">
      <DialogBackground />
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center lg:items-center lg:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95"
            enter-to="opacity-100 translate-y-0 lg:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 lg:scale-100"
            leave-to="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-background text-left shadow-xl transition-all lg:my-8 lg:w-full lg:max-w-2xl">
              <div class="bg-background px-4 pt-5 pb-4 lg:p-6 lg:pb-4">
                <div class="lg:flex lg:items-start">
                  <div
                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 lg:mx-0 lg:h-10 lg:w-10">
                    <ExclamationTriangleIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 text-center lg:mt-0 lg:ml-4 lg:text-left w-full grow">
                    <DialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-gray-900"
                      v-html="
                        errorTitle || $t('Oops, something went wrong. Please try again or contact our support team.')
                      ">
                    </DialogTitle>
                    <div class="mt-2 flex flex-col gap-4">
                      <div v-for="item of getItemsWithErrorDetails(cartStore.error)">
                        <div class="rounded border border-red-500 p-2 flex flex-col gap-2">
                          <TicketInfo :ticket_id="item.ticket_id" />
                          <hr />
                          <p class="font-medium text-red-500">{{ item.error.detail }}</p>
                        </div>
                        <p class="text-sm text-foreground/25">#{{ item.ticket_id }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-50 px-4 py-3 flex flex-col lg:flex-row lg:justify-end lg:px-6 gap-3">
                <button
                  v-if="profileIncomplete"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-base font-semibold text-septenary shadow-sm hover:bg-primary lg:w-auto"
                  @click="
                    cartStore.error = null;
                    navigateToProfile();
                  ">
                  {{ $t("My profile") }}
                </button>
                <button
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-base font-semibold text-septenary shadow-sm hover:bg-primary lg:w-auto"
                  @click="cartStore.error = null">
                  {{ $t("Continue") || "button text goes here" }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { useCartStore } from "@/store/cart";
import TicketInfo from "@/components/ticket/TicketInfo.vue";
import { useUserStore } from "@/store/user.js";
import { useI18n } from "vue-i18n";
import DialogBackground from "@/components/shared/DialogBackground.vue";
import { useRouter } from "vue-router";

const { t } = useI18n();
const userStore = useUserStore();
const cartStore = useCartStore();
const router = useRouter();

const hasErrorDetail = (item) => Boolean(item?.error?.detail);

const getItemsWithErrorDetails = (err) => err?.response?.data?.items?.filter(hasErrorDetail);

const navigateToProfile = () => {
  router.push("/user/profile");
};

const profileIncomplete = computed(() => {
  return (
    cartStore.error?.response?.data &&
    (userStore.roles.includes("onboard_admin") || userStore.roles.includes("portal_readonly"))
  );
});

const errorTitle = computed(() => {
  if (profileIncomplete.value) {
    return t(
      "Please complete your company profile with the necessary information so that we can get to work on verifying you. This can take up to 2 business days. Need help ? Please contact our support team."
    );
  } else {
    return "Oops, something went wrong. Please try again or contact our support team.";
  }
});
</script>
